import "./index.css";
import { useEffect } from "react";

import { InitUFOV1 } from "@wac-web/analytics/ufo/initUFO";
import { WacAnalyticsClientProvider } from "@wac-web/analytics/wac-analytics/analytics-client";
import { WacAnalyticsListener } from "@wac-web/analytics/wac-analytics/wac-analytics-listener-provider";
import { CrossFlowProvider } from "@wac-web/components/cross-flow/services";
import { SitesContextProvider } from "@wac-web/components/sites-provider";
import { UserContextProvider } from "@wac-web/components/user-context/user-context-provider";
import { BxpWebStargateClientProvider } from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import { getStargateBaseUrl } from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import {
    AvailableProductsResponse,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";
import { trackRouteUFOV1PageLoad } from "@wac-web/ufo-v1";
import { GridOverlayWrapper } from "@wac-web/utils/grid-overlay";
import { useAnchorLocalStorage } from "@wac-web/utils/local-storage-hook";
import { LocationProvider } from "@wac-web/utils/location-provider";
import { LoggedInUserProvider } from "@wac-web/utils/logged-in-user-provider";
import { OneTrustProvider } from "@wac-web/utils/onetrust-provider";
import { SmartReCaptcha } from "@wac-web/utils/smart-recaptcha";
import { SSRLoginStateProvider } from "@wac-web/utils/ssr-login-state-provider";
import { UserLocale } from "@wac-web/utils/useGeoCurrency";

import UFOSegment from "@atlaskit/react-ufo/segment";
import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";

import { parseRoute } from "./fastshift-portal-routes";

import type { GrowthUnifiedProfile } from "@wac-web/libraries/growth-unified-profile-client/unified-profile.schema";

/**
 * Simplified Layout for Fastshift Portal
 * Only includes properties actually used by fastshift components
 */
export type LayoutProps = {
    analyticsWebClient: AnalyticsWebClient | null;
    location: Location;
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    loggedInContext?: GrowthUnifiedProfile;
    loggedInContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
    hasCloudSessionTokenCookie?: boolean;
    children: React.ComponentType<PageProps>;
    userAgent?: string;
};

/**
 * Simplified PageProps for Fastshift Portal
 * Only includes properties actually used by fastshift components
 */
export type PageProps = {
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    loggedInContext?: GrowthUnifiedProfile;
    loggedInContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
};

export const Layout = (props: LayoutProps) => {
    const { locale, path } = parseRoute(location);
    useAnchorLocalStorage();

    const isBrowser = () => typeof window !== "undefined";
    if (isBrowser()) {
        trackRouteUFOV1PageLoad(path);
    }

    useEffect(() => {
        // Send operational event when static fallback is detected
        if (window.IS_STATIC && props.analyticsWebClient) {
            props.analyticsWebClient.sendOperationalEvent({
                action: "detected",
                actionSubject: "staticWACFallback",
                source: "layout",
                attributes: {
                    route: window.location.pathname,
                    userAgent: props.userAgent,
                    anonymousId: props.analyticsWebClient.getAnonymousId()
                }
            });
        }
    }, [props.analyticsWebClient, props.userAgent]);

    return (
        <OneTrustProvider>
            {process.env["NODE_ENV"] !== "production" && <GridOverlayWrapper />}
            <WacAnalyticsClientProvider
                // @ts-ignore
                analyticsWebClient={props.analyticsWebClient}
                location={props.location}
            >
                <InitUFOV1 />
                <LocationProvider location={props.location}>
                    <UFOSegment name="wac">
                        <SmartReCaptcha>
                            <BxpWebStargateClientProvider
                                baseUrl={getStargateBaseUrl()}
                            >
                                <SSRLoginStateProvider
                                    hasCloudSessionTokenCookie={
                                        props.hasCloudSessionTokenCookie ??
                                        false
                                    }
                                    userAgent={props.userAgent}
                                >
                                    <LoggedInUserProvider>
                                        <WacAnalyticsListener>
                                            <CrossFlowProvider locale={locale}>
                                                <SitesContextProvider>
                                                    <UserContextProvider
                                                        location={
                                                            props.location.href
                                                        }
                                                        meResponse={
                                                            props.userProfile
                                                        }
                                                        meRequest={
                                                            props.userProfileStatus
                                                        }
                                                        userProfileResponse={
                                                            props.userContext
                                                        }
                                                        userProfileRequest={
                                                            props.userContextStatus
                                                        }
                                                        loggedOutContext={
                                                            props.loggedOutContext
                                                        }
                                                        loggedOutContextStatus={
                                                            props.loggedOutContextStatus
                                                        }
                                                        loggedInContext={
                                                            props.loggedInContext
                                                        }
                                                        loggedInContextStatus={
                                                            props.loggedInContextStatus
                                                        }
                                                    >
                                                        <props.children
                                                            {...props}
                                                        />
                                                    </UserContextProvider>
                                                </SitesContextProvider>
                                            </CrossFlowProvider>
                                        </WacAnalyticsListener>
                                    </LoggedInUserProvider>
                                </SSRLoginStateProvider>
                            </BxpWebStargateClientProvider>
                        </SmartReCaptcha>
                    </UFOSegment>
                </LocationProvider>
            </WacAnalyticsClientProvider>
        </OneTrustProvider>
    );
};
